import ActionButton from 'components/common/ActionButton';
import FalconComponentCard from 'components/common/FalconComponentCard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  FormLabel,
  InputGroup,
  Offcanvas,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import {
  faFolderOpen,
  faPlusCircle,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner3 from 'assets/img/illustrations/corner-3.png';
import BasicECharts from 'components/common/BasicEChart';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import segmentHelper from 'helpers/segment-helper';
import { getColor } from 'helpers/utils';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SimpleBarReact from 'simplebar-react';
import { showAlert, showConfirm } from 'stores/modal';
import api from 'util/api';
import filters from 'util/filters';
import logger from 'util/logger';
import SigningPackagesForm from './SigningPackagesForm';
import { useAsyncDebounce } from 'react-table';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';
import { useListSigningPackagesQuery } from 'rtk/packages.slice';
import classNames from 'classnames';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const SigningPackages = () => {
  const [sortBy, setSortBy] = useState('SortOrder');
  const [isAsc, setIsAsc] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const sortEffectSkipRef = useRef(true);
  const lastApiSortRef = useRef({ sortKey: 'Created', sortOrderAsc: false });

  const [preSigningPackage, setPreSigningPackage] = useState(null);
  const [showSigningPackageForm, setShowSigningPackageForm] = useState(false);
  const [formTitle, setFormTitle] = useState(null);
  const [savingSigningPackage, setSavingSigningPackage] = useState(false);
  const dispatch = useDispatch();

  const skip = pageIndex * pageSize;

  const apiSortKey =
    sortBy === 'SortOrder' ? lastApiSortRef.current.sortKey : sortBy;
  const apiSortOrderAsc =
    sortBy === 'SortOrder' ? lastApiSortRef.current.sortOrderAsc : isAsc;

  if (sortBy !== 'SortOrder') {
    lastApiSortRef.current = { sortKey: sortBy, sortOrderAsc: isAsc };
  }

  const {
    data: signingPackagesRes,
    isFetching,
    isLoading,
    isError: signingPackagesLoadError,
    error: signingPackagesError,
    refetch: refetchSigningPackages
  } = useListSigningPackagesQuery({
    skip,
    pageSize,
    keyword: searchKeyword,
    sortKey: apiSortKey,
    sortOrderAsc: apiSortOrderAsc
  });
  const loading = isFetching || isLoading;

  const signingPackagesDisplay = useMemo(() => {
    const items = [...(signingPackagesRes?.Objects || [])];
    if (sortBy === 'SortOrder') {
      items.sort((a, b) => a.SortOrder - b.SortOrder);
    }
    return items;
  }, [signingPackagesRes?.Objects, sortBy]);
  const totalRecords = signingPackagesRes?.TotalRecords ?? 0;
  const endOfResults = !!signingPackagesRes?.EndOfResults;

  const loadSigningPackages = () => refetchSigningPackages();

  const editSigningPackage = sp => {
    setPreSigningPackage(sp);
    setFormTitle('Update Signing Package');
    setShowSigningPackageForm(true);
  };

  const duplicateSigningPackage = sp => {
    let duplicate = Object.assign({}, sp);
    duplicate.isDuplicate = true;
    duplicate.Name = sp.Name + ' (Copy)';
    setPreSigningPackage(duplicate);
    setFormTitle('Duplicate Signing Package');
    setShowSigningPackageForm(true);
  };

  const saveSigningPackage = item => {
    setSavingSigningPackage(true);
    let package_documents_ids = [];
    _.map(item.package_documents, p => {
      package_documents_ids.push(p.GUID);
    });
    if (preSigningPackage == null || !!preSigningPackage.isDuplicate) {
      api.DocumentCollection.add(
        item.package_name,
        '',
        item.notary_fee,
        item.notes,
        package_documents_ids,
        item.Signing_Fees
      )
        .then(() => {
          setSavingSigningPackage(false);
          loadSigningPackages();
          setShowSigningPackageForm(false);
          toast.success('Signing package added successfully', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faUserCheck,
            toastId: 'signing-package'
          });
          //logger.log(res);
        })
        .catch(err => {
          logger.error('Signing Packages page failed to create package', err);
          setSavingSigningPackage(false);
          dispatch(
            showAlert(
              'Error Saving Signing Package',
              'There was a problem saving this Signing Package. Please try again.'
            )
          );
          loadSigningPackages();
        });
    } else {
      //guid, name, description, Amount, notes, doc_type_guids, Signing_Fees
      api.DocumentCollection.update(
        preSigningPackage.GUID,
        item.package_name,
        '',
        item.notary_fee,
        item.notes,
        package_documents_ids,
        item.Signing_Fees,
        null
      )
        .then(() => {
          setSavingSigningPackage(false);
          loadSigningPackages();
          setShowSigningPackageForm(false);
          toast.success('Signing package updated successfully', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faUserCheck,
            toastId: 'signing-package'
          });
        })
        .catch(() => {
          setSavingSigningPackage(false);
          dispatch(
            showAlert(
              'Error Updating Signing Package',
              'There was a problem updating this Signing Package.  Please try again.'
            )
          );
          loadSigningPackages();
        });
    }
  };

  const deleteSigningPackage = signingPackage => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to remove this signing package?',
        res => {
          if (res) {
            api.DocumentCollection.remove(signingPackage.GUID)
              .then(() => {
                loadSigningPackages();
                segmentHelper.track(
                  segmentHelper.events.SIGNING_PACKAGE_DELETED
                );
              })
              .catch(() => {
                showAlert(
                  'Error Deleting Signing package',
                  'There was a problem removing this signing package.  Please try again.'
                );
                loadSigningPackages();
              });
          }
        }
      )
    );
  };

  const addSigningPackage = () => {
    setPreSigningPackage(null);
    setFormTitle('Add new Signing Packages');
    setShowSigningPackageForm(true);
  };

  const searchItems = useAsyncDebounce(e => {
    const keyword = e.target.value.toLowerCase().trim();
    setSearchKeyword(keyword);
    setPageIndex(0);
  }, 400);

  useEffect(() => {
    if (signingPackagesLoadError) {
      logger.error(
        'Signing Packages page failed to load package list',
        signingPackagesError
      );
    }
  }, [signingPackagesLoadError, signingPackagesError]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  useEffect(() => {
    if (sortEffectSkipRef.current) {
      sortEffectSkipRef.current = false;
      return;
    }
    setPageIndex(0);
  }, [sortBy, isAsc]);

  const getData = e => {
    let data = [];
    let i = 1;
    if (e.NotaryFee) {
      data.push({
        id: i,
        value: e.NotaryFee,
        name: 'Notary Fee',
        color: 'primary'
      });
    }
    let colorRange = 1100;
    if (e?.SigningFees) {
      _.map(e.SigningFees, ef => {
        data.push({
          id: ++i,
          value: ef.Amount,
          name: ef.Name,
          color: 'gray-' + colorRange
        });
        colorRange -= 200;
      });
    }
    logger.log('Signing Packages page prepared fee chart data', {
      signingPackageGuid: e?.GUID,
      signingPackageName: e?.Name || e?.DisplayName,
      feeCount: data.length,
      chartData: data
    });
    return data;
  };

  const queryAttr = 'data-rbd-drag-handle-draggable-id';

  const onDragUpdate = update => {
    if (!update.destination) {
      return;
    }
    const draggableId = update.draggableId;

    const domQuery = `[${queryAttr}='${draggableId}']`;
    const draggedDOM = document.querySelector(domQuery);

    if (!draggedDOM) {
      return;
    }
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    background: 'grey',
    backgroundColor: 'grey',
    ...draggableStyle
  });

  const getListStyle = isDraggingOver => ({
    background: isDraggingOver ? 'lightblue' : 'lightgrey',
    position: 'relative'
  });

  const getMappedColor = data => {
    let colorArray = [];
    _.map(data, d => {
      colorArray.push(getColor(d.color));
    });
    return colorArray;
  };

  const getOptions = (data, radius) => ({
    color: getMappedColor(data),

    tooltip: {
      padding: [7, 10],
      transitionDuration: 0,
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1,
      formatter: params =>
        `<strong>${params.data.name}:</strong> ${params.percent}%`
    },
    series: [
      {
        name: filters.formatCurrencyFromServer(
          data.reduce((acc, val) => parseFloat(val.value) + acc, 0)
        ),
        type: 'pie',
        radius,
        avoidLabelOverlap: false,
        emphasis: {
          scale: false
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: getColor('gray-100')
        },
        label: {
          show: true,
          position: 'center',
          formatter: '{a}',
          fontSize: 20,
          color: getColor('dark')
        },
        data
      }
    ]
  });
  /* eslint-disable react/prop-types */
  const MarketShare = ({ data, radius }) => {
    return data ? (
      <Card.Body className="p-0">
        <Row className="justify-content-between g-0 align-items-center align-content-center">
          <Col xs={5} sm={6} md={7} xl={8} xxl className="pe-2">
            {data.map((item, index) => (
              <MarketShareItem item={item} index={index} key={index} />
            ))}
          </Col>
          <Col xs="auto">
            <div className="ps-0 w-100 text-center">
              <BasicECharts
                echarts={echarts}
                options={getOptions(data, radius)}
                style={{ width: '6rem', height: '6rem' }}
              />
            </div>
          </Col>
        </Row>
      </Card.Body>
    ) : (
      <></>
    );
  };
  const MarketShareItem = ({ item, index }) => {
    const { name, value, color } = item;

    //const percentage = ((value * 100) / total).toFixed(0);
    return (
      <Flex
        alignItems="center"
        justifyContent="between"
        className={`fw-semi-bold fs--2 ${index == 0 && 'mt-3'}`}
      >
        <p className="mb-1">
          <FontAwesomeIcon
            icon="circle"
            className={`me-2`}
            style={{ color: getColor(color) }}
          />
          {name}
        </p>
        <div className="d-xxl-none">
          {filters.formatCurrencyFromServer(value)}
        </div>
      </Flex>
    );
  };
  /* eslint-enable react/prop-types */
  const handleDragEnd = result => {
    if (result.source.index !== result.destination.index) {
      const data = signingPackagesDisplay.find(
        data => data.GUID == result.draggableId
      );
      const reorderedPackages = Array.from(signingPackagesDisplay);
      const [removed] = reorderedPackages.splice(result.source.index, 1);
      reorderedPackages.splice(result.destination.index, 0, removed);

      setIsReordering(true);
      api.DocumentCollection.update(
        data.GUID,
        data.Name,
        data.description,
        data.NotaryFee,
        data.AdditionalData,
        data.DocumentTypeGUIDs,
        data.SigningFees,
        result.destination.index
      )
        .then(() => {
          setIsReordering(false);
          toast.success('Signing packages reordered successfully', {
            theme: 'colored',
            position: 'bottom-center',
            icon: faUserCheck,
            toastId: 'signing-package'
          });
          refetchSigningPackages();
        })
        .catch(() => {
          setIsReordering(false);
          setSavingSigningPackage(false);
          dispatch(
            showAlert(
              'Error Reordering Signing Package',
              'There was a problem ordering this Signing Package. Please try again.'
            )
          );
          loadSigningPackages();
        });
    }
  };

  return (
    <>
      {/* <PageHeader
        preTitle="Active Signing Packages"
        title="Signing Packages list"
        description=""
        className="mb-3"
        titleTag="h2"
        image={corner3}
      /> */}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4'}
              title="Signing Packages"
              subtitle="Browse all signing packages"
              endEl={
                <Flex className={'g-2'}>
                  <div>
                    <Form as={Row} className="gx-2 d-flex align-items-center">
                      <Col xs="auto">
                        <FormLabel className="mb-0">Sort by:</FormLabel>
                      </Col>
                      <Col xs="auto">
                        <InputGroup>
                          <Form.Select
                            size="sm"
                            className="pe-5"
                            value={sortBy}
                            onChange={({ target }) => {
                              setSortBy(target.value);
                            }}
                          >
                            {/* <option value="Name">Name</option> */}
                            {/* <option value="NotaryFee">Notary Fee</option> */}
                            <option value="Created">Created</option>
                            <option value="SortOrder">Sort Order</option>
                          </Form.Select>
                          {sortBy !== 'SortOrder' && (
                            <InputGroup.Text
                              as={Button}
                              variant="link"
                              className="border border-300 text-700"
                              onClick={() => setIsAsc(!isAsc)}
                            >
                              <FontAwesomeIcon
                                icon={
                                  isAsc ? 'sort-amount-up' : 'sort-amount-down'
                                }
                              />
                            </InputGroup.Text>
                          )}
                        </InputGroup>
                      </Col>
                    </Form>
                  </div>
                  <div className="mx-1">
                    <Form.Control
                      onChange={e => searchItems(e)}
                      placeholder="Search package..."
                      name="search"
                    ></Form.Control>
                  </div>
                  <div className="mx-1">
                    <Button
                      variant="primary"
                      onClick={() => addSigningPackage()}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} size="sm" /> Add New
                    </Button>
                  </div>
                </Flex>
              }
            />
            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {(signingPackagesDisplay.length > 0 ||
                pageIndex > 0 ||
                totalRecords > 0) &&
                !signingPackagesLoadError &&
                !isLoading && (
                  <Pagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    currentCount={signingPackagesDisplay.length}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    endOfResults={endOfResults}
                    disabled={loading}
                    onRefresh={() => refetchSigningPackages()}
                    loading={loading}
                  />
                )}
              {signingPackagesDisplay.length !== 0 &&
                !signingPackagesLoadError &&
                !isLoading && (
                  <SimpleBarReact>
                    <DragDropContext
                      onDragEnd={handleDragEnd}
                      onDragUpdate={onDragUpdate}
                    >
                      <StrictModeDroppable droppableId="droppable1">
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef}>
                            <Table
                              {...provided.droppableProps}
                              striped
                              bordered
                              className={classNames(
                                'td-start',
                                loading || isReordering
                                  ? 'refreshing-skeleton'
                                  : undefined
                              )}
                            >
                              <colgroup>
                                <col />
                                <col />
                                <col className="d-sm-none d-md-block" />
                                <col />
                              </colgroup>
                              <thead>
                                <tr>
                                  {sortBy === 'SortOrder' && (
                                    <th scope="col"></th>
                                  )}
                                  <th scope="col">Package name</th>
                                  <th scope="col">Fee</th>
                                  <th scope="col">Documents</th>
                                  <th className="text-end" scope="col">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody
                                style={getListStyle(snapshot.isDraggingOver)}
                              >
                                {_.map(signingPackagesDisplay, (e, index) => {
                                  return (
                                    <Draggable
                                      key={e.GUID}
                                      draggableId={e.GUID}
                                      index={index}
                                    >
                                      {provided => (
                                        <tr
                                          key={e.GUID}
                                          className="bg-white"
                                          ref={provided.innerRef}
                                          style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style
                                          )}
                                          {...provided.draggableProps}
                                        >
                                          {sortBy === 'SortOrder' && (
                                            <td style={{ width: '2%' }}>
                                              <div
                                                {...provided.dragHandleProps}
                                              >
                                                <FontAwesomeIcon icon="grip-lines" />
                                              </div>
                                            </td>
                                          )}
                                          <td style={{ width: '15%' }}>
                                            <div> {e.Name} </div>
                                          </td>
                                          <td style={{ width: '20%' }}>
                                            <MarketShare
                                              data={getData(e)}
                                              radius={['100%', '87%']}
                                            />
                                          </td>
                                          <td style={{ width: '37%' }}>
                                            <ul>
                                              {_.map(e.DocumentTypeGUIDs, d => {
                                                let document =
                                                  e?.DocumentTypes?.find(
                                                    item => {
                                                      return item.GUID == d;
                                                    }
                                                  );
                                                if (document) {
                                                  return (
                                                    <li key={d}>
                                                      <span className="fw-medium text-primary">
                                                        {
                                                          document.NotarizationType
                                                        }
                                                      </span>{' '}
                                                      - {document.Value}
                                                    </li>
                                                  );
                                                }
                                                return null;
                                              })}
                                            </ul>
                                          </td>
                                          <td
                                            className="text-end"
                                            style={{ width: '30%' }}
                                          >
                                            <ActionButton
                                              icon="edit"
                                              title="Edit"
                                              label=""
                                              variant="action"
                                              className="p-1 mx-1 text-primary"
                                              onClick={() =>
                                                editSigningPackage(e)
                                              }
                                            />
                                            <ActionButton
                                              icon="copy"
                                              title="Duplicate"
                                              label=""
                                              variant="action"
                                              className="p-1 mx-1 text-primary"
                                              onClick={() =>
                                                duplicateSigningPackage(e)
                                              }
                                            />
                                            <ActionButton
                                              icon="trash-alt"
                                              title="Remove"
                                              label=""
                                              variant="action"
                                              className="p-1 mx-1 text-danger"
                                              onClick={() =>
                                                deleteSigningPackage(e)
                                              }
                                            />
                                          </td>
                                        </tr>
                                      )}
                                    </Draggable>
                                  );
                                })}
                              </tbody>
                            </Table>
                            <div style={{ background: 'lightBlue' }}>
                              {provided.placeholder}
                            </div>
                          </div>
                        )}
                      </StrictModeDroppable>
                    </DragDropContext>
                  </SimpleBarReact>
                )}
              {signingPackagesDisplay.length === 0 &&
                !loading &&
                signingPackagesDisplay.length === 0 &&
                (signingPackagesLoadError ? (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faFolderOpen} size="3x" />
                    <br />
                    <p className="my-3">
                      Unable to load signing package list. Try again
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faFolderOpen} size="3x" />
                    <br />
                    <p className="my-3">
                      You haven't created any signing packages yet. <br />
                      Click "Add new" to create new signing packages
                    </p>
                  </div>
                ))}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
      <Offcanvas
        size="lg"
        id="offcanvas-address-form"
        show={showSigningPackageForm}
        backdrop="static"
        onHide={() => setShowSigningPackageForm(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{formTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <hr className="my-0" />
        <Offcanvas.Body>
          <SigningPackagesForm
            preSigningPackage={preSigningPackage}
            saveSigningPackage={saveSigningPackage}
            setShowSigningPackagesForm={setShowSigningPackageForm}
            savingSigningPackage={savingSigningPackage}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default SigningPackages;
